import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, Circle, Lock, Send, Undo2, ClipboardCheck, ExternalLink, User, AlertTriangle, Repeat } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';

export function OutreachStepsCard({ projectId, stage, onStageChanged }) {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [skipOpen, setSkipOpen] = useState(false);
  const [skipReason, setSkipReason] = useState('');
  const [skipTarget, setSkipTarget] = useState('contacted');
  const [skipBusy, setSkipBusy] = useState(false);

  const load = useCallback(() => {
    api.getOutreachSteps(projectId).then(r => setData(r.data)).catch(() => {});
  }, [projectId]);
  useEffect(() => { load(); }, [load, stage]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener('outreach-tasks-changed', h);
    return () => window.removeEventListener('outreach-tasks-changed', h);
  }, [load]);

  if (!data) return null;
  const showSkip = ['olta_approved', 'outreach_wip'].includes(stage);
  if (!data.applicable && !showSkip) return null;

  const canAct = (s) => isAdmin || s.anyone || (s.who_ids || []).includes(user?.id);
  const steps = data.steps || [];
  const doneCount = steps.filter(s => s.done).length;
  const SKIP_TARGETS = [
    ['contacted', 'Contacted'],
    ['response_received', 'Response received'],
    ['first_call_meeting', 'First Call / Meeting'],
  ];

  const proposeSkip = async () => {
    if (skipReason.trim().length < 5) { toast.error('Bitte kurz begründen (z.B. brokered Deal, Meeting steht bereits)'); return; }
    setSkipBusy(true);
    try {
      const r = await api.proposeSkipOutreach(projectId, skipReason.trim(), skipTarget);
      toast.success(r.data.message);
      setSkipOpen(false); setSkipReason('');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
    setSkipBusy(false);
  };

  const cancelSkip = async () => {
    setSkipBusy(true);
    try {
      await api.overrideSkipOutreach(projectId);
      toast.success('Skip-Vorschlag zurückgezogen');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
    setSkipBusy(false);
  };

  const tick = async (key) => {
    setBusy(key);
    try {
      const r = await api.tickOutreachStep(projectId, key);
      if (r.data.new_stage_label) {
        const seq = r.data.sequence;
        if (seq?.status === 'started') {
          toast.success(`Sent! Deal moved to ${r.data.new_stage_label} — follow-up sequence started for ${seq.contact_name}`);
        } else if (seq?.status === 'no_contact') {
          toast.warning('Sent & moved to Contacted — but no Owner contact is linked, so no follow-up sequence was started');
        } else {
          toast.success(`Sent! Deal moved to ${r.data.new_stage_label}`);
        }
        if (onStageChanged) onStageChanged();
      } else {
        if (key === 'review') {
          toast.success('Reviewed & approved — next: print the flyer & pack the envelope', {
            action: { label: 'Open Outreach page', onClick: () => navigate('/outreach') },
            duration: 6000,
          });
        } else {
          toast.success('Step done');
        }
      }
      setData(r.data);
      window.dispatchEvent(new Event('outreach-flow-changed'));
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((d && typeof d === 'object' && d.message) || (typeof d === 'string' && d) || 'Could not complete step', { duration: 6000 });
    } finally { setBusy(null); }
  };

  const untick = async (key) => {
    setBusy(key);
    try {
      const r = await api.untickOutreachStep(projectId, key);
      setData(r.data);
      window.dispatchEvent(new Event('outreach-flow-changed'));
      toast.success('Step re-opened');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not re-open step');
    } finally { setBusy(null); }
  };

  return (
    <div className="bg-card border border-border rounded-sm overflow-hidden mb-6" data-testid="outreach-steps-card">
      <div className="flex items-center gap-3 p-4 border-b border-border">
        <div className="w-8 h-8 rounded-sm bg-emerald-500/20 flex items-center justify-center">
          <ClipboardCheck className="w-4 h-4 text-emerald-400" />
        </div>
        <div className="flex-1 min-w-0">
          <span className="font-heading text-base text-foreground">Outreach Checklist</span>
          <p className="text-[11px] text-muted-foreground">All green → one click sends the deal to Contacted</p>
        </div>
        {data.applicable && (
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-600/15 border border-emerald-600/30 text-emerald-400 font-mono"
          data-testid="outreach-steps-progress">
          {doneCount}/{steps.length}
        </span>
        )}
      </div>
      <div className="p-4 space-y-1.5">
        {data.sequence_check && (
          data.sequence_check.has_contact === false ? (
            <div className="flex items-start gap-2 p-2.5 rounded-sm border border-amber-500/40 bg-amber-500/5 mb-2" data-testid="sequence-check-no-contact">
              <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
              <p className="text-[11px] text-amber-300">
                No Owner contact linked to this deal — the follow-up sequence (email → LinkedIn → call) can't start automatically after sending. Link the owner in the Contacts card first.
              </p>
            </div>
          ) : data.sequence_check.missing?.length > 0 ? (
            <div className="flex items-start gap-2 p-2.5 rounded-sm border border-amber-500/40 bg-amber-500/5 mb-2" data-testid="sequence-check-missing">
              <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
              <div className="text-[11px] text-amber-300 space-y-1 min-w-0">
                <p>
                  <button onClick={() => navigate(`/contacts?q=${encodeURIComponent(data.sequence_check.contact_name)}`)}
                    data-testid="sequence-check-contact-link"
                    className="underline underline-offset-2 hover:text-amber-100 font-medium">
                    {data.sequence_check.contact_name}
                  </button>
                  : missing <span className="font-medium">{data.sequence_check.missing.join(', ')}</span> — private address,
                  email &amp; phone are mandatory before the letter can be finalized (LinkedIn is optional).
                </p>
                {data.sequence_check.changes?.length > 0 && (
                  <p className="text-amber-300/80" data-testid="sequence-check-changes">
                    Follow-up sequence adapts automatically: {data.sequence_check.changes.join(' · ')}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2 p-2.5 rounded-sm border border-emerald-500/25 bg-emerald-500/5 mb-2" data-testid="sequence-check-ready">
              <Repeat className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
              <p className="text-[11px] text-emerald-300">
                Follow-up sequence ready — starts automatically for {data.sequence_check.contact_name} once the letter is marked as sent.
                {data.sequence_check.template_name && (
                  <span className="text-emerald-300/80" data-testid="sequence-check-template"> Sequenz: <span className="font-medium">{data.sequence_check.template_name}</span>{data.sequence_check.legacy ? ' (Legacy — Deal vor der Prio-Umstellung freigegeben)' : ''}.</span>
                )}
                {data.sequence_check.changes?.length > 0 && <span className="text-emerald-300/70"> {data.sequence_check.changes.join(' · ')}</span>}
              </p>
            </div>
          )
        )}
        {steps.map((s, i) => {
          const isCurrent = i === data.current_index;
          const locked = !s.done && !isCurrent;
          const isSent = s.key === 'sent';
          return (
            <div key={s.key}
              className={`flex items-start gap-3 p-2.5 rounded-sm border transition-colors ${
                s.done ? 'border-emerald-500/25 bg-emerald-500/5'
                : isCurrent ? 'border-amber-500/40 bg-amber-500/5'
                : 'border-border/50 bg-secondary/20 opacity-60'}`}
              data-testid={`outreach-step-${s.key}`}>
              <span className="mt-0.5 shrink-0">
                {s.done ? <CheckCircle2 className="w-5 h-5 text-emerald-400" strokeWidth={1.5} />
                  : locked ? <Lock className="w-4 h-4 text-muted-foreground/40 mt-0.5" strokeWidth={1.5} />
                  : <Circle className="w-5 h-5 text-amber-400" strokeWidth={1.5} />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className={`text-sm font-medium ${s.done ? 'text-foreground/60 line-through' : 'text-foreground'}`}>
                    {i + 1}. {s.title}
                  </p>
                  <span className="text-[9px] px-1.5 py-0.5 rounded border border-border bg-secondary text-muted-foreground font-mono flex items-center gap-1">
                    <User className="w-2.5 h-2.5" /> {s.who}{s.anyone ? ' · jeder kann übernehmen' : ''}
                  </span>
                  {s.skipped && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 font-mono" title={s.hint} data-testid={`outreach-step-skipped-${s.key}`}>
                      entfällt — Schreiber ist finaler Approver
                    </span>
                  )}
                  {s.done && s.done_by && (
                    <span className="text-[9px] text-muted-foreground/60 font-mono">
                      by {s.done_by}{s.done_at ? ` · ${new Date(s.done_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}` : ''}
                    </span>
                  )}
                </div>
                {!s.done && <p className="text-[11px] text-muted-foreground mt-0.5">{s.hint}</p>}
                {isCurrent && !s.manual && (
                  <Button size="sm" variant="outline" onClick={() => navigate('/outreach')}
                    className="h-6 px-2 text-[10px] rounded-sm border-border hover:border-blue-500/50 hover:text-blue-400 mt-1.5"
                    data-testid={`outreach-step-open-${s.key}`}>
                    <ExternalLink className="w-3 h-3 mr-1" /> Open Outreach page
                  </Button>
                )}
                {isCurrent && s.manual && canAct(s) && !isSent && (
                  <Button size="sm" onClick={() => tick(s.key)} disabled={busy === s.key}
                    className="h-6 px-2.5 text-[10px] rounded-sm bg-amber-600 hover:bg-amber-500 text-white mt-1.5"
                    data-testid={`outreach-step-btn-${s.key}`}>
                    {busy === s.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <><CheckCircle2 className="w-3 h-3 mr-1" /> {s.key === 'enrich' ? 'Prüfen & abhaken' : 'Mark done'}</>}
                  </Button>
                )}
                {isCurrent && isSent && canAct(s) && (
                  <Button onClick={() => tick(s.key)} disabled={busy === s.key}
                    className="h-8 px-4 text-xs rounded-sm bg-blue-600 hover:bg-blue-500 text-white font-medium mt-2"
                    data-testid="outreach-step-btn-sent">
                    {busy === s.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1.5" /> Mark as sent → Contacted</>}
                  </Button>
                )}
                {isCurrent && s.manual && !canAct(s) && (
                  <p className="text-[10px] text-muted-foreground/60 mt-1 italic">Waiting on {s.who} (or an admin)</p>
                )}
              </div>
              {s.done && s.manual && s.done_by && canAct(s) && (
                <button onClick={() => untick(s.key)} disabled={busy === s.key}
                  className="text-muted-foreground/40 hover:text-amber-400 transition-colors shrink-0 mt-0.5"
                  title="Re-open this step" data-testid={`outreach-step-undo-${s.key}`}>
                  {busy === s.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />}
                </button>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-3 pt-2 border-t border-border/60">
        {data.skip_proposed ? (
          <div className="border border-amber-500/40 bg-amber-500/5 rounded-sm p-2.5" data-testid="skip-outreach-pending">
            <p className="text-[11px] text-amber-300">
              Outreach-Skip vorgeschlagen von {data.skip_proposed.proposed_by} → {(SKIP_TARGETS.find(t => t[0] === data.skip_proposed.target_stage) || ['', data.skip_proposed.target_stage])[1]}
              {' — wartet auf 4-Augen-Freigabe unter Pending Reviews.'}
            </p>
            {data.skip_proposed.reason && (
              <p className="text-[10px] text-amber-300/70 italic mt-0.5">„{data.skip_proposed.reason}"</p>
            )}
            {(isAdmin || data.skip_proposed.proposed_by_id === user?.id) && (
              <button onClick={cancelSkip} disabled={skipBusy} data-testid="skip-outreach-cancel-btn"
                className="text-[10px] underline text-muted-foreground hover:text-red-400 mt-1">
                Vorschlag zurückziehen
              </button>
            )}
          </div>
        ) : skipOpen ? (
          <div className="border border-border rounded-sm p-2.5 space-y-2" data-testid="skip-outreach-form">
            <p className="text-[11px] text-foreground/80 font-medium">Outreach überspringen (4-Augen-Prinzip)</p>
            <textarea value={skipReason} onChange={e => setSkipReason(e.target.value)} rows={2}
              placeholder="Grund — z.B. brokered Deal, kein Brief nötig / Meeting mit Verkäufer steht bereits"
              data-testid="skip-outreach-reason-input"
              className="w-full bg-background border border-border rounded-sm px-2 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-amber-500/50" />
            <div className="flex items-center gap-2 flex-wrap">
              <select value={skipTarget} onChange={e => setSkipTarget(e.target.value)} data-testid="skip-outreach-target-select"
                className="bg-background border border-border rounded-sm px-2 py-1 text-[11px] text-foreground focus:outline-none">
                {SKIP_TARGETS.map(([v, l]) => <option key={v} value={v}>Ziel: {l}</option>)}
              </select>
              <Button size="sm" onClick={proposeSkip} disabled={skipBusy} data-testid="skip-outreach-submit-btn"
                className="h-6 px-2.5 text-[10px] rounded-sm bg-amber-600 hover:bg-amber-500 text-white">
                {skipBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Vorschlagen'}
              </Button>
              <button onClick={() => setSkipOpen(false)} className="text-[10px] text-muted-foreground hover:text-foreground underline">Abbrechen</button>
            </div>
            <p className="text-[10px] text-muted-foreground/70">
              Ein Deal-Approver muss den Skip unter Pending Reviews freigeben — erst dann springt der Deal weiter.
            </p>
          </div>
        ) : (
          <button onClick={() => setSkipOpen(true)} data-testid="skip-outreach-open-btn"
            className="text-[10px] text-muted-foreground/70 hover:text-amber-400 transition-colors underline decoration-dotted">
            Outreach überspringen (z.B. brokered Deal) — 4-Augen
          </button>
        )}
      </div>
    </div>
  );
}
